import {useState, useEffect, useRef} from "react";
import axios from 'axios';
import download from 'downloadjs';
import { saveAs } from 'file-saver';
import {CdaWebModalTabs} from './cdawebmodaltabs.js';
import {CdaWebModalTimeSelector} from './cdawebmodaltimeselector.js';
import {CdaWebModalTable} from './cdawebmodaltable.js';
import {CdaWebModalWarningBox} from './cdawebmodalwarningbox.js';
import {CdaWebModalErrorBox} from './cdawebmodalerrorbox.js';
import {CDAERRORCODES, CDAWebDataType, CDAWebDataFileTypes, CDAWebAudioFileTypes, MAXIMUMTABS} from './../../constants.js';
import {CdaWebModalGraphButtonsRow} from './cdawebmodalgraphbuttonsrow.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { convertPdfImageToPngImage } from "./pdfconverter";

/**
 * Modal content holds the content/components interacting and display data from CDAWeb server.
 *
 * @param props.title String title of modal.
 * @param props.variables Array of String that is used as query parameters.
 * @param props.url String where to get the data from.
 * @param props.cdasId String Id of dataset.
 * @param props.initialStartTime Datetime string that indicates the start time of interest.
 * @param props.initialStopTime Datetime string that indicates the stop time of interest.
 * @param props.sessionState Array of data that is used to save state of plots.
 * @param props.setSessionState Function that deals with setting the sessionState parameter.
 * @return JSX
 */
export function CdaWebModalContent(props) {
    const {title, timelineInfo, variables, url, cdasId, initialStartTime, initialStopTime, sessionState, setSessionState} = props;
    let visibleContent, errorContent;
    let savedState = {};
    let currentTabValue = 0;

    if(sessionState) {
        if(!sessionState.data) {
          savedState['data'] = null;
        } else if(sessionState.data[sessionState.data.length-1] === sessionState?.latestData) {
            savedState['data'] = sessionState.data;
        } else {
            savedState['data'] = [...(sessionState?.data), sessionState?.latestData];
        }
        savedState['currentCheckedRows'] = sessionState?.currentCheckedRows;
        savedState['currentCheckedOptions'] = sessionState?.currentCheckedOptions;
        savedState['currentSelectedImageType'] = sessionState?.currentSelectedImageType;
        currentTabValue = savedState.data ? savedState.data.length-1 : 0;
    }

    const [data, setData] = useState(savedState.data || null);
    const [loading, setLoading] = useState(false);
    const [plotError, setPlotError] = useState(null);
    const [checkedRows, setCheckedRows] = useState(sessionState?.currentCheckedRows || []);
    const [checkedOptions, setCheckedOptions] = useState(sessionState?.currentCheckedOptions || []);
    const [selectedImageType, setSelectedImageType] = useState(sessionState?.currentSelectedImageType ||'png');
    const [currentTab, setCurrentTab] = useState(currentTabValue);
    const [startTime, setStartTime] = useState(sessionState?.currentStartTime || initialStartTime);
    const [stopTime, setStopTime] = useState(sessionState?.currentStopTime  || initialStopTime);
    const [lastPlotRemoved, setLastPlotRemoved] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [filteredVariables, setFilteredVariables] = useState(variables);
    const [highResolutionArr, setHighResolutionArr] = useState(sessionState?.currentHighResolutionArr || []);
    const loadingIconRef = useRef(null);

    useEffect(() => {
        const filterVariables = () => {
            if (filterText.trim() === '') {
                return variables;
            }

            const filterTerms = filterText.split(',').map(term => term.trim().toLowerCase());
            return variables
                .filter(varName => filterTerms.some(term => varName.Name.toLowerCase().includes(term)
                                                            ||
                                                            varName.LongDescription.toLowerCase().includes(term)));
        };

        if (loading && loadingIconRef.current) {
            loadingIconRef.current.scrollIntoView({ behavior: "smooth" });
            loadingIconRef.current.focus();
        }

        let fV = filterVariables();

        setFilteredVariables(fV);
    }, [filterText, variables, loading]);

    const onStartTimeChange = (value) => {
        const cleaned = formatTime(value);
        console.log("Setting startTime to:", cleaned);
        setStartTime(cleaned);
    };

    const onStopTimeChange = (value) => {
        const cleaned = formatTime(value);
        console.log("Setting stopTime to:", cleaned);
        setStopTime(cleaned);
    };

    const updateDataState = (newData, curStartTime, curStopTime, replaceLatest = false) => {
        const lData = replaceLatest ? newData[newData.length-1] : data[data.length-1] || null;
        const nData = newData ? [...newData] : null;
        const  newStateObj = {
                              data: nData,
                              currentStartTime: curStartTime,
                              currentStopTime: curStopTime,
                              currentCheckedRows: checkedRows,
                              currentCheckedOptions: checkedOptions,
                              currentSelectedImageType: selectedImageType,
                              latestData: lData};
        setData(nData);
        setSessionState(newStateObj);
    };

    const replaceDataPNGImage = (tabIndex, newImage) => {
        const nData = data
        nData[tabIndex].png = newImage;

        const  newStateObj = {
                              data: nData,
                              currentStartTime: sessionState.currentStartTime,
                              currentStopTime: sessionState.currentStopTime,
                              currentCheckedRows: sessionState.currentCheckedRows,
                              currentCheckedOptions: sessionState.currentCheckedOptions,
                              currentSelectedImageType: sessionState.currentSelectedImageType,
                              latestData: sessionState.latestData};
        setData(nData);
        setSessionState(newStateObj);
    };

    const simulateTabKey = () => {
        const focusableElements = Array.from(
            document.querySelectorAll(
               'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
        );

        const currentIndex = focusableElements.findIndex((el) => el === document.activeElement);
        const nextIndex = (currentIndex + 1) % focusableElements.length;
        focusableElements[nextIndex]?.focus();
    };

    const displayLoading = () => {
        setPlotError(null);
        setLoading(true);
    }

    const handleError = (error) => {
        setPlotError({status:error.status, message: error.message});
        setLoading(false);
    }

    const removeTab = (tabIndex) => {
        if(data.length < 2) {
            updateDataState(null, startTime, stopTime, false);
        } else {
            updateDataState(data.filter((x,i) => i!==tabIndex), startTime, stopTime, false);
        }

        if(tabIndex === data.length-1) {
            setLastPlotRemoved(true);
        }
        setHighResolutionArr(highResolutionArr.filter((_, index) => index !== tabIndex));
        setCurrentTab(0);
    }

    const handleDeleteData = (index) => {
        removeTab(index);
    }

    const handleNewWindow = (imageType) => {
        const sImageTypeUrl = data[currentTab][imageType];

        if(!sImageTypeUrl)
        {
            setPlotError({status: '002'});
            return;
        }

        try{
            const width = window.innerWidth;
            const height = window.innerHeight;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;

            window.open(sImageTypeUrl, '_blank', `width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`);
            removeTab(currentTab);

            setPlotError(null);
        } catch(error) {
            error['insight'] = "New window error";
            setPlotError({status:'Unknown', message:error.message});
        }
    }

    const handleDownload = async (imageType) => {

        const sImageTypeUrl = data[currentTab][imageType];

        console.log(`Downloading: ${sImageTypeUrl} image: ${imageType} url: ${JSON.stringify(data[currentTab])}`);
        if(!sImageTypeUrl)
        {
            setPlotError({status: '002'});
            return;
        }

        try {
            const res = await axios.get(sImageTypeUrl, {
                responseType: 'blob',
            });

            let imageBlob = res.data;
            const name = sImageTypeUrl.substring(sImageTypeUrl.lastIndexOf('/') + 1);
            const filename = name;

            download(imageBlob, filename, 'image/png');
            setPlotError(null);
        } catch(error) {
            console.log(`There is a download ERROR: ${error}`);
            error['insight'] = "Download error";
            setPlotError(error);
        }
    }

    const handleCDADownload = async (fileDescription) => {
        if(!fileDescription || !fileDescription.Name) {
            setPlotError({status: '006', message:''});
            return;
        }

        try {
            console.log(`Downloading from CDA: ${fileDescription.Name}`);
            const res = await axios.get(fileDescription.Name, {
                responseType: 'blob',
            });
            const filename = fileDescription.Name.substring(fileDescription.Name.lastIndexOf('/') + 1);

            saveAs(res.data, filename);
            setPlotError(null);
        } catch(error) {
            console.log(`There is a cda download ERROR: ${error}`);
            error['insight'] = "Download error";
            setPlotError(error);
        }
    }

    const handlePan = async (isLeft = true) => {
        displayLoading();

        let quarterTimeDif = getTimeDifference(startTime, stopTime) / (1000 * 4);
        const shiftSeconds = isLeft ? -quarterTimeDif : quarterTimeDif;

        const newStartStr = getUtcTimeShift(startTime, shiftSeconds);
        const newStopStr  = getUtcTimeShift(stopTime, shiftSeconds);

        if (newStartStr && newStopStr) {
            onStartTimeChange(newStartStr);
            onStopTimeChange(newStopStr);

            await onGetDataFromCDAWeb(
                CDAWebDataType.IMAGE,
                lastPlotRemoved ? null : data.length - 1,
                newStartStr,
                newStopStr,
                true
            );
       }
   };

    const handleZoom = async (isZoomIn = true) => {
        displayLoading();

        const durationSec = getTimeDifference(startTime, stopTime) / 1000;
        const quarterDurationSec  = durationSec / 4;
        const halfDurationSec  = durationSec / 2;

        const shiftSec = isZoomIn ? quarterDurationSec : -halfDurationSec;

        const newStartStr = getUtcTimeShift(startTime, shiftSec);
        const newStopStr  = getUtcTimeShift(stopTime, -shiftSec);  // opposite direction

        if (newStartStr && newStopStr) {
            onStartTimeChange(newStartStr);
            onStopTimeChange(newStopStr);

            await onGetDataFromCDAWeb(
                CDAWebDataType.IMAGE,
                lastPlotRemoved ? null : data.length - 1,
                newStartStr,
                newStopStr,
                true
            );
        }
    };

    const onGetDataFromCDAWeb = async (dataType = CDAWebDataType.IMAGE, replaceIndex = null, newStrTime = null, newStpTime = null, currentTabReplacement = false) => {
        try {
            let baseURL = 'https://cdaweb.gsfc.nasa.gov/WS/cdasr/1/';
            let url = baseURL;

            //Variables
            let varNames = checkedRows.map((i)=>{
                         return `${filteredVariables[i].Name}`;
                        }) || [];

            if(varNames.length === 0 ) {
                setPlotError({status:'003'});
                return;
            }

            const optionsQueryIds = checkedOptions.map(el=>{return el.queryId});

            //Options
            let optionsValue = "";
            let cdfVersion = 3;
            let getHiRes = optionsQueryIds.includes('highres');
            let newHiResArr = [...highResolutionArr];

            if(optionsQueryIds.includes('bigplot') && optionsQueryIds.includes('nonoise')) {
                optionsValue = "&options=3";
            } else if(optionsQueryIds.includes('bigplot')) {
                optionsValue = "&options=2";
            } else if(optionsQueryIds.includes('nonoise')) {
                optionsValue = "&options=1";
            } else if(optionsQueryIds.includes('cvc')) {
                cdfVersion = 2;
            }

            if(getHiRes) {
                if(replaceIndex) {
                    newHiResArr[replaceIndex] = true;
                } else if(currentTabReplacement) {
                    newHiResArr[currentTab] = true;
                } else {
                    newHiResArr.push(true);
                }
            } else {
                if(replaceIndex) {
                    newHiResArr[replaceIndex] = false;
                } else if(currentTabReplacement) {
                    newHiResArr[currentTab] = false;
                } else {
                    newHiResArr.push(false);
                }
            }

            //Request
            let nsrt = newStrTime ? newStrTime : startTime; //Get latest time. Note useState's startTime has not updated yet.
            let nspt = newStpTime ? newStpTime : stopTime;  //Get latest time. Note useState's stopTime has not updated yet.
            const startTm = nsrt.replaceAll('-','').replaceAll(':','') + 'Z';
            const stopTm = nspt.replaceAll('-','').replaceAll(':','') + 'Z';
            let fUrl, pngUrl, pdfUrl;
            let response, responsePNG, responsePDF;


            if(dataType === CDAWebDataType.IMAGE) {
                fUrl = (url+`dataviews/sp_phys/datasets/${cdasId}/data/${startTm},${stopTm}/${varNames}?format=${selectedImageType}${optionsValue}&cdfVersion=3`);
                pngUrl = (url+`dataviews/sp_phys/datasets/${cdasId}/data/${startTm},${stopTm}/${varNames}?format=png${optionsValue}&cdfVersion=3`);

                //If another filetype is requested by user, we still need to fetch pdf file from CDAWeb (a scalable vector image file)
                //which can be edited, saved, and displayed as a clearer png file.
                //Prevent redundant request for pdf file too.
                if(selectedImageType !== "pdf" && getHiRes) {
                    pdfUrl = (url+`dataviews/sp_phys/datasets/${cdasId}/data/${startTm},${stopTm}/${varNames}?format=pdf${optionsValue}&cdfVersion=3`);
                    responsePDF = await axios.get(pdfUrl);
                }

                responsePNG = await axios.get(pngUrl);
            } else if(CDAWebDataFileTypes.includes(dataType) || CDAWebAudioFileTypes.includes(dataType)) {
                fUrl = (url+`dataviews/sp_phys/datasets/${cdasId}/data/${startTm},${stopTm}/${varNames}?format=${dataType}${optionsValue}&cdfVersion=${cdfVersion}`);
            }

            response = await axios.get(fUrl);


            if(response.data.Error.length>0 || !response.data.FileDescription) {
                if(dataType === CDAWebDataType.IMAGE) {
                    const message = response.data.Error?.join('. ') + '.';
                    setPlotError({status: '001' , message});
                } else if (CDAWebDataFileTypes.includes(dataType)){
                    const message = response.data.Status?.join('. ') + '.';
                    setPlotError({status: '006', message});
                }
                return;
            }

            if(dataType === CDAWebDataType.IMAGE && responsePNG.data.Error.length>0) {
                const message = responsePNG.data.Error.join('. ') + '.';
                setPlotError({status: '001' , message});
                return;
            }


            //Update state
            if(dataType === CDAWebDataType.IMAGE) {
                const imageUrls = {};

                //If pdf file is requested, response from fUrl can be used
                if(selectedImageType === "pdf") {
                    imageUrls[selectedImageType] = response.data.FileDescription[0].Name;
                } else if(getHiRes) {
                    imageUrls["pdf"] = responsePDF.data.FileDescription[0].Name;
                }
                imageUrls['png'] = responsePNG.data.FileDescription[0].Name;

                if(data) {
                    if(replaceIndex !== null){
                      const newDataState = [...(data.slice(0,data.length-1)), imageUrls];
                      updateDataState(newDataState, nsrt, nspt, true);
                      setCurrentTab(newDataState.length - 1);
                    } else if(data.length < MAXIMUMTABS) {
                      const newDataState = [...data , imageUrls];
                      updateDataState(newDataState, nsrt, nspt, true);
                      setCurrentTab(newDataState.length - 1);
                    } else {
                      const newDataState = [...(data.slice(1, MAXIMUMTABS)), imageUrls];
                      updateDataState(newDataState, nsrt, nspt, true);
                      setCurrentTab(newDataState.length - 1);
                    }
                } else {
                    updateDataState([imageUrls], nsrt, nspt, true);
                }
                setHighResolutionArr(newHiResArr);
                setPlotError(null);
            } else if(CDAWebDataFileTypes.includes(dataType) || CDAWebAudioFileTypes.includes(dataType)) {
                await handleCDADownload(response.data.FileDescription[0]);
            }
        } catch (error) {
            setPlotError({status: error.response?.status || 'Unknown', message: error.message});
        } finally {
            setLoading(false);
            setLastPlotRemoved(false);
        }
    }

    if (loading) {
        const message = "Request has been made to CDAWeb. . .Waiting for response";
        visibleContent = getLoadingJSX(message, loadingIconRef);
    }

    if (plotError) {
       errorContent = getPlotErrorJSX(plotError);
    }

    if (data) {
       visibleContent = (
             <div className="cdaWebModalPlotSection">
               <div>
                 <CdaWebModalTabs
                   tabsData={data}
                   defaultTab={currentTab}
                   deleteData={handleDeleteData}
                   updatePNGImageUrl ={replaceDataPNGImage}
                   setCurrentTab={setCurrentTab}
                   enhanceImageArr={highResolutionArr}/>
               </div>
               {data[currentTab] && <div>
                 <CdaWebModalGraphButtonsRow
                   title = {title}
                   loading={loading}
                   handleNewWindow={handleNewWindow}
                   handleDownload={handleDownload}
                   handlePan={handlePan}
                   handleZoom={handleZoom}
                   availableImageTypes={data.length > 0 && data[currentTab] ? Object.keys(data[currentTab]) : []}/>
               </div>}
             <br />
             <br />
             </div>);
    }


     return (
       <div className="">
         <div className="cdaWebModalTitle">
           {title}
         </div>

         <CdaWebModalTimeSelector
           timelineInfo={timelineInfo}
           startTime = {startTime}
           stopTime = {stopTime}
           onStartTimeChange = {onStartTimeChange}
           onStopTimeChange = {onStopTimeChange} />
         <br />
         <div className="cdaWebModalInputContainer">
           <input
             type="text"
             placeholder="Filter variables..."
             value={filterText}
             onChange={e => setFilterText(e.target.value)}
             className="cdaWebModalVariableInput"
             spellCheck="false"
           />
           <FontAwesomeIcon icon={faSearch} className="cdaWebModalVariableInputIcon" />
         </div>
         <CdaWebModalTable
           variables={filteredVariables}
           url = {url}
           startTime = {startTime}
           stopTime = {stopTime}
           checkedRows = {checkedRows}
           setCheckedRows = {setCheckedRows}
           title = {title}

           onGetDataFromCDAWeb = {onGetDataFromCDAWeb}
           handleError = {handleError}
           checkedOptions = {checkedOptions}
           setCheckedOptions = {setCheckedOptions}
           loading={loading}
           displayLoading = {displayLoading}
           sImageType = {selectedImageType}
           setSImageType = {setSelectedImageType} />

          <br />
          {errorContent}
          {visibleContent}
       </div>
    );
}


/**
 * Function that gets the difference in time of two datetimes.
 *
 * @param props.a String datetime string.
 * @param props.b String datetime string.
 * @return Number
 */
function getTimeDifference(a, b) {
    const dateA = new Date(a);
    const dateB = new Date(b);

    return dateB.getTime() - dateA.getTime();
}


/**
 * Function that moves time 'datetimeString' by amount 'seconds' into its past or future.
 *
 * @param props.datetimeString String datetime string that is to be time shifted
 * @param props.seconds Number the amount of time to add or subtract. Can be positive or negative.
 * @return String
 */
function getUtcTimeShift(datetimeString, seconds) {
    try {
        // Accept almost any ISO variant → parse correctly as UTC
        let iso = datetimeString.trim();

        // If it has no timezone, assume UTC and add Z
        if (!/[Z+-]/.test(iso.slice(10))) {
            iso += (iso.includes('.') ? '' : '.000') + 'Z';
        }

        const date = new Date(iso);
        if (isNaN(date.getTime())) {
            throw new Error('Invalid date: ' + datetimeString);
        }

        const newMs = date.getTime() + seconds * 1000;
        const newDate = new Date(newMs);

        // Always output clean format without ms or Z
        return newDate.toISOString().replace(/\.\d{3}Z$/, '');
    } catch (e) {
        console.error("Shift error:", e.message, "Input:", datetimeString);
        return datetimeString; // fallback to avoid breaking UI
    }
}


/**
 * Function that returns JSX for loading.
 * @param {React.RefObject} [loadingIconRef=null] - Optional ref to the loading container.
 * @return JSX
 */
function getLoadingJSX(message, loadingIconRef=null) {
    return (

      <div ref={loadingIconRef} tabIndex={-1} className="cdaWebModalLoadingContainer">
        <div className="cdaWebModalLoadingColumn">
          <div className="cdaWebModalLoadingSpinner"></div>
          {message && <div className="cdaWebModalLoadingText">{message}</div>}
          <br/>
        </div>
      </div>
    );
}

/**
 * Function that returns JSX for error.
 * @param plotError Object that contains 'status' and 'message'
 * @return JSX
 */
function getPlotErrorJSX(plotError) {
    let errorContent = (<div>Error</div>);

    if(`${plotError.status}` === '001' || `${plotError.status}` === '006') {
        errorContent = <CdaWebModalWarningBox title= {CDAERRORCODES[`${plotError.status}`][0]} body={CDAERRORCODES[`${plotError.status}`][1]} insightMessage = {plotError.message.split('.')[0]+'.'} />
    } else if(`${plotError.status}` === '002') {
        errorContent = <CdaWebModalWarningBox title= {CDAERRORCODES['002'][0]} body={CDAERRORCODES['002'][1]} />
    } else if(parseInt(`${plotError.status}`) < 100) {
        errorContent = <CdaWebModalWarningBox title= {CDAERRORCODES[`${plotError.status}`][0]} body={CDAERRORCODES[`${plotError.status}`][1]} />
    } else if( Object.keys(CDAERRORCODES).includes(`${plotError.status}`)) {
        errorContent = <CdaWebModalErrorBox title= {CDAERRORCODES[plotError.status][0]} body={plotError.message} />
    } else {
        errorContent = <CdaWebModalErrorBox title= {CDAERRORCODES['Unknown'][0]} body={CDAERRORCODES['Unknown'][1]} />
    }

    return errorContent;
}


/**
 * Formats a datetime string to include leading zeros in seconds if needed.
 * @param String dateTime - The datetime string to format (in the format "YYYY-MM-DDTHH:mm:ss").
 * @returns String The formatted datetime string with leading zeros in seconds if necessary.
 */
function formatTime(dateTime) {
    let dtStr;

    if (dateTime instanceof Date) {
        dtStr = dateTime.toISOString();
    } else if (typeof dateTime === 'string') {
        dtStr = dateTime;
    } else {
        console.warn("Unexpected type in formatTime:", dateTime);
        return dateTime || '';
    }

    // Normalize: remove .xxxZ or .xxx or Z
    dtStr = dtStr
        .replace(/\.\d{1,3}(Z)?$/, '')   // strip .000Z / .000 / Z
        .replace(/Z$/, '');              // just in case

    console.log(`Normalized input to formatTime: ${dtStr}`);

    const [date, time] = dtStr.split('T');
    if (!time) return dtStr;

    let [hours, minutes, seconds] = time.split(':');
    hours   = hours   || '00';
    minutes = minutes || '00';
    seconds = seconds ? seconds.split('.')[0] || '00' : '00';  // drop any lingering .xxx

    // Pad if needed (though your inputs should already be padded)
    return `${date}T${hours.padStart(2,'0')}:${minutes.padStart(2,'0')}:${seconds.padStart(2,'0')}`;
}
